import { useState } from 'react';
import { useInstall } from '../lib/install.js';
import { Button } from './ui.js';

const DISMISSED_KEY = 'padhai:install-dismissed';

function wasDismissed(): boolean {
  try {
    return window.localStorage.getItem(DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * "Install Padhai" across the top of the signed-in app, until it is installed
 * or the person says not now. On Chrome it is one tap to the browser's own
 * install dialog; on an iPhone, where no page can install itself, it says
 * where the button is. Anywhere else, nothing.
 *
 * Remembered per device: a shared classroom phone dismissed once stays
 * dismissed for every student who signs in on it.
 */
export function InstallBanner() {
  const state = useInstall();
  const [dismissed, setDismissed] = useState(wasDismissed);

  if (dismissed || state.kind === 'installed' || state.kind === 'unavailable') return null;

  const dismiss = () => {
    setDismissed(true);
    try {
      window.localStorage.setItem(DISMISSED_KEY, '1');
    } catch {
      // Private mode: it comes back next visit, which is harmless.
    }
  };

  return (
    <aside
      aria-label="Install Padhai"
      className="no-print rise mb-5 flex items-center gap-3 rounded-card bg-surface p-3 pr-2 shadow-card sm:p-4"
    >
      <img
        src="/icons/icon-192.png"
        alt=""
        width={44}
        height={44}
        className="size-11 shrink-0 rounded-control"
      />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold">Get the Padhai app</p>
        <p className="text-xs leading-snug text-ink-soft">
          {state.kind === 'ios' ? (
            <>
              Tap <span className="font-medium text-ink">Share</span>, then{' '}
              <span className="font-medium text-ink">Add to Home Screen</span>.
            </>
          ) : (
            'One tap from your home screen, full screen, and quicker to open.'
          )}
        </p>
      </div>
      {state.kind === 'prompt' ? (
        <Button
          variant="primary"
          size="sm"
          onClick={() => {
            void state.install();
          }}
        >
          Install
        </Button>
      ) : null}
      <button
        type="button"
        onClick={dismiss}
        aria-label="Not now"
        className="flex size-9 shrink-0 items-center justify-center rounded-full text-ink-faint hover:bg-surface-sunken hover:text-ink"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          className="size-4"
          aria-hidden="true"
        >
          <path d="M6 6l12 12M18 6 6 18" />
        </svg>
      </button>
    </aside>
  );
}
